import { Col, Input, Row } from 'antd'
import { Button, ButtonActionBar } from 'modules/core/components'
import CreateStrategyModal from './CreateStrategyModal'
import ImportStrategiesModal from './ImportStrategiesModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ApprovalRulesTable from './ApprovalRulesTable'
import { useTranslation } from 'react-i18next'
import configuration from 'modules/configuration'
import { connect } from 'react-redux'
import { client } from 'httpclient'
import _ from 'lodash'

const { Search } = Input

const buildParams = (search, filters) => {
  const params = {}
  if (search) params.search = search
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value
    }
  })
  return params
}

const ReleaseStrategies = ({
  onRefetchKpis,
  fetchStrategiesList,
  fetchAreasList,
  fetchAllDimensions,
  fetchAllDimensionsValues,
  areaList,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [mode, setMode] = useState('create')
  const [selectedRule, setSelectedRule] = useState({})
  const [deleteId, setDeleteId] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const [filters, setFilters] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [documentTypes, setDocumentTypes] = useState([])
  const [purchaseGroups, setPurchaseGroups] = useState([])
  const [societies, setSocieties] = useState([])
  const isFirstFetchRef = useRef(true)
  const { t } = useTranslation()

  useEffect(() => {
    fetchAreasList()
    client.get('/cnr-approvals/document-types/').then((response) => {
      const data = response.data
      const items = Array.isArray(data) ? data : data?.results ?? data?.data ?? []
      setDocumentTypes(items)
    })
    fetchAllDimensions().then((response) => {
      const groupPurchaseId = response.payload.data.find((el) => el.name === 'group_purchase')?.id
      const societyId = response.payload.data.find((el) => el.name === 'sociedad')?.id
      if (groupPurchaseId) {
        fetchAllDimensionsValues({ id_dimension: groupPurchaseId }).then((r) =>
          setPurchaseGroups(r.payload.data || []),
        )
      }
      if (societyId) {
        fetchAllDimensionsValues({ id_dimension: societyId }).then((r) =>
          setSocieties(r.payload.data || []),
        )
      }
    })
  }, [fetchAreasList, fetchAllDimensions, fetchAllDimensionsValues])

  const runFetch = useCallback(
    (search, f) => {
      setIsLoading(true)
      return fetchStrategiesList(buildParams(search, f)).finally(() => setIsLoading(false))
    },
    [fetchStrategiesList],
  )

  const debouncedFetch = useMemo(() => _.debounce(runFetch, 400), [runFetch])

  useEffect(() => {
    if (isFirstFetchRef.current) {
      isFirstFetchRef.current = false
      runFetch(searchValue, filters)
    } else {
      setIsLoading(true)
      debouncedFetch(searchValue, filters)
    }
    return () => debouncedFetch.cancel()
  }, [searchValue, filters, debouncedFetch, runFetch])

  const refetch = () => runFetch(searchValue, filters)

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <ButtonActionBar>
            <Search
              style={{ width: 200 }}
              placeholder={t('ACTION_SEARCH')}
              allowClear
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <Button.Default
              title="CONFIG_APPROVALS_IMPORT_ACTION"
              onClick={() => setShowImportModal(true)}
            />
            <Button.Primary
              title={t('CONFIG_APPROVALS_CREATE_STRATEGY_ACTION')}
              onClick={() => {
                setShowCreateModal(true)
                setMode('create')
              }}
            />
          </ButtonActionBar>
        </Col>
        <Col span={24}>
          <ApprovalRulesTable
            setMode={setMode}
            setSelectedRule={setSelectedRule}
            setShowCreateModal={setShowCreateModal}
            setDeleteId={setDeleteId}
            deleteId={deleteId}
            filters={filters}
            setFilters={setFilters}
            filterOptions={{
              documentTypes,
              areas: areaList,
              purchaseGroups,
              societies,
            }}
            tableLoading={isLoading}
            onRefetch={() => {
              refetch()
              onRefetchKpis()
            }}
          />
        </Col>
      </Row>
      <CreateStrategyModal
        visible={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        mode={mode}
        initialData={selectedRule}
        onRefetch={() => {
          refetch()
          onRefetchKpis()
        }}
      />
      <ImportStrategiesModal
        visible={showImportModal}
        onCancel={() => setShowImportModal(false)}
        setIsTableLoading={setIsLoading}
        onSuccessUpload={() => {
          refetch()
          onRefetchKpis()
        }}
      />
    </>
  )
}

const mapStateToProps = (state) => ({
  areaList: configuration.selectors.getAreaList(state),
})

const mapDispatchToProps = {
  fetchStrategiesList: configuration.actions.fetchStrategiesList,
  fetchAreasList: configuration.actions.fetchAreasList,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
}

export default connect(mapStateToProps, mapDispatchToProps)(ReleaseStrategies)
